import { FormControlProps, OutlinedInputProps } from "@mui/material";
import { UnitType as UnitTypeEnum } from "@sinch/core/entities/serverEnums";
import type { UnitType } from "@sinch/core/entities/serverEnums.types";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { MdsKeyboardArrowDown, MdsKeyboardArrowUp } from "@sinch/icons";
import { collectErrorMessagesFromFields } from "@sinch/utils/react-hook-form/errorHandling";
import { propEq } from "ramda";
import { isNotNil } from "ramda-adjunct";
import React, { FC, useMemo } from "react";
import { get, useFormState } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormControlWrapper, InputWrapperProps, MdsIcon } from "@sinch/components";
import { useUnitType } from "@sinch/components/application/Rate/useUnitType";
import {
  IntervalOutlinedInput,
  IntervalSelectContainer,
  ResponsiveAdornment,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/components/styled";
import { usePopoverState } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/hooks";
import { IntervalPopover } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/IntervalPopover/IntervalPopover";
import { RateIntervals } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.graphql";
import { RateIntervalsQuery } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/queries.types";
import { ReadOnlyInput } from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/ReadOnlyInput";
import type {
  IntervalOption,
  IntervalSelectValue,
} from "@sinch/components/application/RatelistBuilder/RateBuilderRowContent/IntervalSelect/types";
import { RateBuilderTreeFormFields } from "@sinch/components/application/RatelistBuilder/ratelistBuilderSchema";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";
import { InputSkeleton } from "@sinch/components/form/InputSkeleton/InputSkeleton";
import { getLeafList } from "@sinch/components/form/MultilevelSelect/hooks/optionTreeTools";

/**
 * Properties for the IntervalSelect component.
 */
type IntervalSelectProps = Omit<OutlinedInputProps, "onChange" | "value"> &
  InputWrapperProps & {
    FormControlProps?: FormControlProps;
    onChange: (value: IntervalSelectValue | null) => void;
    value: IntervalSelectValue | null;
  };

/**
 * IntervalSelect is a React functional component used for rendering an interval selection interface.
 * It provides users the ability to select intervals, add custom intervals, and manage additional settings.
 */
export const IntervalSelect: FC<IntervalSelectProps> = ({
  value,
  onChange,
  onBlur,
  errorMessage,
  name = "",
  ...props
}) => {
  const { t } = useTranslation();
  const { rateType, disableConditions } = useRateBuilderSettings();
  const { data, loading } = useQueryHandler<RateIntervalsQuery>(RateIntervals, { ratelistType: rateType });

  const { getOptionsList } = useUnitType(rateType);
  const validation = useIntervalSelectValidation(name, errorMessage);
  const { open, anchorEl, setAnchorEl, handleToggleOpen, handleClickAway } = usePopoverState(onBlur);

  const selectedCustomIntervalId = value?.unit.customIntervalId ?? null;

  const options = useMemo(
    () =>
      createIntervalOptions(data, getOptionsList, selectedCustomIntervalId, {
        ...(!disableConditions
          ? {
              noInterval: {
                label: t("RateBuilder.noUnitOption"),
                value: null,
                description: t("RateBuilder.noUnitDescription"),
              },
            }
          : {}),
        addCustom: {
          label: t("RateBuilder.addCustomUnitOption"),
          value: { type: UnitTypeEnum.Custom, customIntervalId: "__addCustomInterval__" },
          description: null,
          group: getOptionsList().find(propEq("value", UnitTypeEnum.Custom))?.label,
        },
      }),
    [data, getOptionsList, selectedCustomIntervalId, t]
  );

  return (
    <IntervalSelectContainer>
      <div ref={setAnchorEl}>
        <FormControlWrapper error={validation.hasError} errorMessage={validation.errorMessages}>
          <InputSkeleton active={loading}>
            <IntervalOutlinedInput
              data-cy="IntervalSelect-input"
              endAdornment={
                <ResponsiveAdornment position="end">
                  <MdsIcon fontSize="small" icon={open ? MdsKeyboardArrowUp : MdsKeyboardArrowDown} />
                </ResponsiveAdornment>
              }
              fullWidth
              isValueNull={value === null}
              onClick={(e) => {
                if (props.readOnly) {
                  return;
                }
                handleToggleOpen(e);
              }}
              slotProps={{
                input: { options: getLeafList(options) ?? [] },
              }}
              slots={{ input: ReadOnlyInput }}
              value={value}
              {...props}
              error={validation.hasError}
            />
          </InputSkeleton>
        </FormControlWrapper>
      </div>
      <IntervalPopover
        anchorEl={anchorEl}
        onChange={onChange}
        onClickAway={handleClickAway}
        onToggleOpen={handleToggleOpen}
        open={open}
        options={options}
        value={value}
      />
    </IntervalSelectContainer>
  );
};

/**
 * Generates a list of interval options based on provided data and configuration.
 *
 * Archived custom units are not offered for new rates, but the current selection is kept so it keeps
 * its label.
 */
const createIntervalOptions = (
  data: RateIntervalsQuery | undefined,
  getOptionsList: () => { value: string; label: string; description: string }[],
  selectedCustomIntervalId: string | null,
  staticOptions: {
    noInterval?: IntervalOption;
    addCustom: IntervalOption;
  }
): IntervalOption[] => {
  const customIntervals = [
    ...(data?.rateIntervals ?? [])
      .filter(({ archived, id }) => !archived || id === selectedCustomIntervalId)
      .map(({ id, name, description }) => ({
        label: name,
        value: { type: UnitTypeEnum.Custom, customIntervalId: id },
        description,
        group: getOptionsList().find(propEq("value", UnitTypeEnum.Custom))?.label,
      })),
    staticOptions.addCustom,
  ];

  const intervalTypeOptions = getOptionsList().map((item) => {
    if (item.value === UnitTypeEnum.Custom) {
      return null;
    }

    return {
      ...item,
      value: { type: item.value as UnitType, customIntervalId: null },
    };
  });

  return [staticOptions.noInterval, ...intervalTypeOptions, ...customIntervals].filter(isNotNil);
};

/**
 * A custom hook for render validation of fields in a form. It retrieves errors
 * related to specific fields, compiles them into an array of error messages, and provides
 * a flag indicating whether any errors are present.
 */
const useIntervalSelectValidation = (name: string, errorMessage?: string[]) => {
  const { errors } = useFormState<RateBuilderTreeFormFields>();
  const unitError = get(errors, `${name}.unit`);
  const minimumError = get(errors, `${name}.minimum`);

  const errorMessages = [
    ...collectErrorMessagesFromFields(unitError),
    ...collectErrorMessagesFromFields(minimumError),
    ...(errorMessage ?? []),
  ].filter((element) => isNotNil(element));

  return {
    errorMessages,
    hasError: !!(unitError || minimumError),
  };
};
